/**
 * Copy to Clipboard Hook
 *
 * Provides a reusable pattern for copying text to clipboard
 * with visual feedback (copied state) and optional toast notification.
 *
 * @module hooks/useCopyToClipboard
 */

import { useState, useCallback, useRef } from 'react';

import { useToastStore } from '@/store/useToasts';

interface UseCopyToClipboardOptions {
  /** Duration to show copied state (ms) - default 2000 */
  resetDelay?: number;
  /** Whether to show a success toast - default true */
  showToast?: boolean;
  /** Custom success message for toast */
  successMessage?: string;
  /** Show fallback prompt if clipboard fails - default false */
  showFallbackPrompt?: boolean;
}

interface UseCopyToClipboardReturn {
  /** Whether content was recently copied (boolean) */
  copied: boolean;
  /** Which label was recently copied (for multi-button UIs) */
  copiedLabel: string | null;
  /** Copy text to clipboard */
  copy: (text: string, label?: string) => Promise<boolean>;
  /** Reset the copied state */
  reset: () => void;
  /** Check if a specific label was just copied */
  isCopied: (label: string) => boolean;
}

/**
 * Hook for copying text to clipboard with visual feedback
 *
 * @example
 * ```tsx
 * // Simple usage (single button)
 * const { copied, copy } = useCopyToClipboard();
 * return (
 *   <Button onClick={() => copy(apiKey, 'API Key')}>
 *     {copied ? <Check /> : <Copy />}
 *   </Button>
 * );
 *
 * // Multi-button usage (track which was copied)
 * const { isCopied, copy } = useCopyToClipboard();
 * return (
 *   <>
 *     <Button onClick={() => copy(jsonUrl, 'JSON')}>
 *       {isCopied('JSON') ? <Check /> : <Copy />}
 *     </Button>
 *     <Button onClick={() => copy(csvUrl, 'CSV')}>
 *       {isCopied('CSV') ? <Check /> : <Copy />}
 *     </Button>
 *   </>
 * );
 * ```
 */
export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {}
): UseCopyToClipboardReturn {
  const { resetDelay = 2000, showToast = true, successMessage, showFallbackPrompt = false } = options;
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const { success } = useToastStore();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    setCopiedLabel(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const copy = useCallback(
    async (text: string, label?: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedLabel(label ?? 'copied');

        if (showToast) {
          const message = successMessage ?? (label ? `${label} copied to clipboard` : 'Copied to clipboard');
          success(message);
        }

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Reset copied state after delay
        timeoutRef.current = setTimeout(() => {
          setCopiedLabel(null);
          timeoutRef.current = null;
        }, resetDelay);

        return true;
      } catch {
        // Clipboard API failed (permissions denied, insecure context, etc.)
        if (showFallbackPrompt) {
          window.prompt(`Copy ${label ?? 'text'}:`, text);
        }
        return false;
      }
    },
    [resetDelay, showToast, successMessage, success, showFallbackPrompt]
  );

  const isCopied = useCallback((label: string) => copiedLabel === label, [copiedLabel]);

  return {
    copied: copiedLabel !== null,
    copiedLabel,
    copy,
    reset,
    isCopied,
  };
}
